﻿import axios from "axios";
import {
  AUTH_EVENTS,
  clearAuth,
  emit,
  getToken,
} from "./token.js";
import {
  LOCAL_API_URL,
  PRODUCTION_API_URL,
  apiOriginOf,
  resolveApiBaseUrl,
} from "./baseUrl.js";

/**
 * Base URL every request hangs off. `VITE_API_URL` is inlined by Vite at
 * build time, so changing it needs a rebuild, not a restart. When it is
 * missing the deployed Render API is used for a production build and the local
 * backend for `npm run dev` — the branch is on `import.meta.env.PROD`, so a
 * production bundle never carries a localhost fallback.
 *
 * Accepts a bare origin or the full `.../api` base — see services/baseUrl.js.
 */
export const API_BASE_URL =
  resolveApiBaseUrl(import.meta.env.VITE_API_URL) ??
  (import.meta.env.PROD ? PRODUCTION_API_URL : LOCAL_API_URL);

/** Same URL without the `/api` suffix, shown on the login screen. */
export const API_ORIGIN = apiOriginOf(API_BASE_URL);

/**
 * Normalised error thrown by every failed call, so components can rely on a
 * single shape instead of poking at axios internals.
 *
 * The backend's error envelope (see backend/src/middleware/errorHandler.js) is
 * `{ success: false, error: { code, message, details? } }`.
 */
export class ApiError extends Error {
  constructor({ message, code, status, details }) {
    super(message);
    this.name = "ApiError";
    this.code = code ?? "UNKNOWN_ERROR";
    this.status = status ?? 0;
    this.details = Array.isArray(details) ? details : details ? [details] : [];
  }

  /** Field-level messages, ready to hand to a form. */
  get fieldErrors() {
    return this.details.filter((d) => typeof d === "string");
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  get isValidation() {
    return this.status === 422 || this.code === "VALIDATION_ERROR";
  }
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

/**
 * Endpoints that legitimately answer 401 as part of normal operation — a failed
 * login must not trigger a global "session expired" logout.
 */
const ANONYMOUS_PATHS = ["/auth/login", "/auth/register"];

const isAnonymousRequest = (url = "") =>
  ANONYMOUS_PATHS.some((path) => url.includes(path));

/* -------------------------------------------------------------------------- */
/* Request: attach the Bearer token                                           */
/* -------------------------------------------------------------------------- */

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* -------------------------------------------------------------------------- */
/* Response: unwrap `{ success, data }`                                       */
/* -------------------------------------------------------------------------- */

api.interceptors.response.use(
  (response) => {
    // 204 No Content (all DELETE endpoints) — no envelope to unwrap.
    if (response.status === 204) return null;

    const body = response.data;

    // Non-JSON passthrough (proxies, health checks outside /api).
    if (body === null || typeof body !== "object" || !("success" in body)) {
      return body;
    }

    if (body.success === false) {
      throw new ApiError({
        message: body.error?.message ?? "Request failed",
        code: body.error?.code,
        status: response.status,
        details: body.error?.details,
      });
    }

    // `data` is omitted by the backend when null (see utils/ApiResponse.js).
    return body.data ?? null;
  },

  (error) => {
    if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    const { response, config } = error;

    if (!response) {
      // No response at all: DNS failure, TLS error, CORS rejection, or timeout.
      const timedOut = error.code === "ECONNABORTED";
      return Promise.reject(
        new ApiError({
          message: timedOut
            ? "The salon server took too long to respond. Please try again."
            : "Cannot reach the salon server. Check your connection and try again.",
          code: timedOut ? "TIMEOUT" : "NETWORK_ERROR",
          status: 0,
        }),
      );
    }

    const body = response.data;
    const envelope =
      body && typeof body === "object" && body.error ? body.error : null;

    const apiError = new ApiError({
      message:
        envelope?.message ||
        (response.status === 404
          ? "That record could not be found."
          : response.statusText) ||
        "Request failed",
      code: envelope?.code,
      status: response.status,
      details: envelope?.details,
    });

    // 401 on a protected route means the token is missing, invalid or expired:
    // drop it and let AuthContext bounce the user back to /login.
    if (response.status === 401 && !isAnonymousRequest(config?.url)) {
      clearAuth();
      emit(AUTH_EVENTS.unauthorized, { code: apiError.code });
    }

    return Promise.reject(apiError);
  },
);

export default api;
